/**
 * Quick add — POSTs to /cart/add.js for icon-only ATC controls.
 * On success dispatches `product_atc_via_quick_add` for the quick cart.
 */
(() => {
  if (window.__themeQuickAdd) return;
  window.__themeQuickAdd = true;

  const ROOT = window.Shopify?.routes?.root || '/';
  const enqueueCartMutation = (() => {
    if (window.themeEnqueueCartMutation) return window.themeEnqueueCartMutation;

    let queue = Promise.resolve();
    window.themeEnqueueCartMutation = (mutation) => {
      const next = queue.then(mutation, mutation);
      queue = next.catch(() => {});
      return next;
    };

    return window.themeEnqueueCartMutation;
  })();

  const rootOf = (button) => button.closest('.quick-add');

  const setBusy = (button, busy) => {
    const root = rootOf(button);

    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');

    if (root) {
      root.classList.toggle('quick-add--loading', busy);
    }
  };

  const giftItemsFor = (paidVariantId, giftVariantId) => {
    const paid = Number(paidVariantId) || 0;
    const gift = Number(giftVariantId) || 0;

    if (gift <= 0 || gift === paid) return [];
    if (window.themeFreeGift?.isPromotionSuppressed?.(String(gift))) return [];

    return [
      {
        id: gift,
        quantity: 1,
        properties: {
          _gwp: 'yes',
          _gwp_promotion: String(gift),
        },
      },
    ];
  };

  const addToCart = async (variantId, quantity, giftVariantId) => {
    const items = [
      { id: Number(variantId), quantity: Number(quantity) || 1 },
      ...giftItemsFor(variantId, giftVariantId),
    ];

    const postItems = (payloadItems) =>
      enqueueCartMutation(() =>
        fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: payloadItems }),
        }),
      );

    const paidItem = items[0];
    let response = await postItems(items);
    let data = await response.json().catch(() => ({}));

    if (!response.ok && items.length > 1) {
      response = await postItems([paidItem]);
      data = await response.json().catch(() => ({}));
    }

    if (!response.ok) {
      // A rejected add can still put part of the quantity in the cart (up to a
      // stock or basket limit), so ask quick cart and the header count to catch up.
      document.dispatchEvent(new CustomEvent('cart_refresh'));

      const message = data.description || data.message || 'Unable to add to cart';
      throw new Error(message);
    }

    return data;
  };

  const blockDisabledQuickAddLink = (event) => {
    const link = event.target instanceof Element
      ? event.target.closest('a.quick-add__button[aria-disabled="true"]')
      : null;

    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
  };

  document.addEventListener('click', blockDisabledQuickAddLink, true);
  document.addEventListener('auxclick', blockDisabledQuickAddLink, true);

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-quick-add]')
      : null;

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');

    if (!variantId) return;

    event.preventDefault();

    if (button.hasAttribute('data-quick-add-drawer')) {
      const productUrl = button.getAttribute('data-product-url');

      if (productUrl) {
        const openEvent = new CustomEvent('quick_add_drawer_open', {
          bubbles: true,
          cancelable: true,
          detail: {
            url: productUrl,
            variantId,
            opener: button,
          },
        });

        if (!button.dispatchEvent(openEvent)) return;

        // Nothing cancelled the event, which usually means the drawer module
        // has not upgraded yet. Falling through here would silently add a
        // variant the shopper never chose, so wait for the drawer instead.
        const drawer = document.querySelector('quick-add-drawer');

        if (drawer) {
          customElements.whenDefined('quick-add-drawer').then(() => {
            drawer.open?.({ url: productUrl, variantId, opener: button });
          });

          return;
        }
      }
    }

    const quantity = button.getAttribute('data-quantity') || '1';
    const giftVariantId = button.getAttribute('data-free-gift-variant-id');

    setBusy(button, true);

    addToCart(variantId, quantity, giftVariantId)
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent('product_atc_via_quick_add', {
            bubbles: true,
            detail: { items: data.items || data, source: 'quick-add' },
          })
        );
      })
      .catch((error) => {
        const root = rootOf(button);

        if (root) {
          root.classList.add('quick-add--error');
          window.setTimeout(() => {
            root.classList.remove('quick-add--error');
          }, 1600);
        }

        document.dispatchEvent(
          new CustomEvent('cart_error', {
            bubbles: true,
            detail: { message: error?.message || '', source: 'quick-add' },
          })
        );
      })
      .finally(() => {
        setBusy(button, false);
      });
  });
})();
